import i18n from '../i18n'
import { weekdayInitials } from './progress'

/**
 * The settings screen's rules (design page 18, `settings`; P-settings of
 * the go-live plan), mirrored from the Android app's `SettingsScreen.kt`,
 * `ReminderSummary.kt` and `ThemeMode.kt` so both clients write the same
 * summary under the reminder and flip the auto theme at the same hour.
 * Plain functions, not components — they read `i18n.t()` directly, the
 * `settings` namespace.
 */

const t = (key, options) => i18n.t(key, { ns: 'settings', ...options })

/** The design's four default speeds; the practice page may offer more, the default is one of these. */
export const DEFAULT_SPEEDS = [0.5, 0.75, 1, 1.25]

export const REMINDER_STEP_MINUTES = 15
export const MINUTES_PER_DAY = 24 * 60

/** Saturday-first, like `weekMinutes`: workdays are Saturday–Wednesday, the weekend Thursday and Friday. */
export const EVERY_DAY = [true, true, true, true, true, true, true]
export const WORKDAYS = [true, true, true, true, true, false, false]
export const WEEKEND = [false, false, false, false, false, true, true]

/** Functions, not a constant: the active language decides the labels. */
export const themeOptions = () => [
  { value: 'light', label: t('theme.light') },
  { value: 'dark', label: t('theme.dark') },
  { value: 'auto', label: t('theme.auto') },
]

/** The design's rule for AUTO: dark from 19:00 until 07:00, device time. */
export const NIGHT_STARTS_HOUR = 19
export const NIGHT_ENDS_HOUR = 7

export function isNightHour(hourOfDay) {
  return hourOfDay >= NIGHT_STARTS_HOUR || hourOfDay < NIGHT_ENDS_HOUR
}

/** Which palette a mode asks for at an hour — pure, so the 19:00/07:00 boundary is a test. */
export function isDarkFor(mode, hourOfDay) {
  if (mode === 'light') return false
  if (mode === 'dark') return true
  return isNightHour(hourOfDay)
}

const pad2 = (value) => String(value).padStart(2, '0')

/** A time of day as HH:MM in Latin digits — a clock the learner sets and reads back stays LTR. */
export function clock24(minuteOfDay) {
  const minute = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  return `${pad2(Math.floor(minute / 60))}:${pad2(minute % 60)}`
}

/** «الان بر اساس ساعت دستگاه: 21:00 — تاریک». */
export function autoThemeNow(hourOfDay) {
  return t('autoThemeNow', {
    time: `${pad2(hourOfDay)}:00`,
    state: isNightHour(hourOfDay) ? t('theme.dark') : t('theme.light'),
  })
}

/** Playback speed as the design writes it: 0.5x, 0.75x, 1x, 1.25x — Latin. */
export function speedLabel(multiplier) {
  return `${Number(multiplier)}x`
}

/** The reminder's time moves in quarter hours and wraps around midnight. */
export function stepMinutes(minuteOfDay, direction) {
  return (minuteOfDay + direction * REMINDER_STEP_MINUTES + MINUTES_PER_DAY) % MINUTES_PER_DAY
}

/**
 * What the reminder's day row says about seven booleans, in the design's
 * order of precedence: every day, none, the two named presets, then the
 * initials of whatever is ticked.
 */
export function summarizeDays(days) {
  const count = days.filter(Boolean).length
  if (count === 7) return { kind: 'every' }
  if (count === 0) return { kind: 'none' }
  if (count === 5 && days.slice(0, 5).every(Boolean)) return { kind: 'workdays' }
  if (count === 2 && days[5] && days[6]) return { kind: 'weekend' }
  return { kind: 'some', indices: days.map((on, index) => (on ? index : -1)).filter((index) => index >= 0) }
}

export function daysLabel(days) {
  const summary = summarizeDays(days)
  switch (summary.kind) {
    case 'every':
      return t('days.every')
    case 'none':
      return t('days.none')
    case 'workdays':
      return t('days.workdays')
    case 'weekend':
      return t('days.weekend')
    default: {
      const initials = weekdayInitials()
      return summary.indices.map((index) => initials[index]).join('، ')
    }
  }
}

/** «روزهای کاری · 21:00» under the reminder's title, or «خاموش». */
export function reminderSummary(reminder) {
  if (!reminder?.enabled) return t('reminderOff')
  return `${daysLabel(reminder.days)} · ${clock24(reminder.minuteOfDay)}`
}

/** JS counts from Sunday; the reminder's week starts on Saturday. */
export function saturdayFirstIndex(date) {
  return (date.getDay() + 1) % 7
}

/**
 * Whether the reminder is due at this very minute: enabled, today ticked,
 * and the clock on its minute. The caller remembers the day it fired, so a
 * page that stays open does not ring twice.
 */
export function reminderDueAt(reminder, date) {
  if (!reminder?.enabled) return false
  if (!reminder.days?.[saturdayFirstIndex(date)]) return false
  return date.getHours() * 60 + date.getMinutes() === reminder.minuteOfDay
}

/** The reminder's own words (the Android notification's title and body). */
export const reminderTitle = () => t('reminderNotification.title')
export const reminderBody = () => t('reminderNotification.body')

/** The microphone row's value, from the Permissions API's state. */
export function micStatus(state) {
  switch (state) {
    case 'granted':
      return { label: t('mic.granted'), tone: 'good' }
    case 'denied':
      return { label: t('mic.denied'), tone: 'bad' }
    case 'prompt':
      return { label: t('mic.prompt'), tone: 'muted' }
    default:
      return { label: t('mic.unknown'), tone: 'muted' }
  }
}
